import type { ReactElement } from "react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import type { ZodRawShape } from "zod";

import type { FormDefinition } from "../types.js";
import Form from "./Form.js";
import QueryProvider from "./QueryProvider.js";
import { usePluginFormSubmission } from "./usePluginFormSubmission.js";

/**
 * Initialize a form in a container.
 *
 * @template T The type of the form schema.
 * @param definition The form definition to use for the form display.
 * @param formContainer The form element to render the form in.
 */
export function init<T extends ZodRawShape>(
	definition: FormDefinition<T>,
	formContainer: HTMLDivElement,
): void {
	const reactRoot = createRoot(formContainer);
	reactRoot.render(
		<StrictMode>
			<QueryProvider>
				<ShortcodeForm definition={definition} />
			</QueryProvider>
		</StrictMode>,
	);
}

/**
 * A form component that can be used in a shortcode.
 *
 * @template T The type of the form schema.
 * @param props The props for the form component.
 * @param props.definition The form definition to use for the form display.
 * @returns The form component.
 */
function ShortcodeForm<T extends ZodRawShape>(props: {
	definition: FormDefinition<T>;
}): ReactElement {
	const mutator = usePluginFormSubmission(props.definition);
	return <Form formDefinition={props.definition} formMutator={mutator} />;
}
